import {useDisclosure} from '@mantine/hooks';
import type {MRT_RowSelectionState} from 'mantine-react-table';
import React, {useEffect, useMemo, useState} from 'react';
import type {InspectionScheduleResponse} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useBulkSetInspectionScheduleActiveMutation} from 'src/redux/queries/inspectionSchedule';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface UseScheduleBulkActiveOptions {
  /** Rows currently on the page; selection is reconciled against their ids. */
  rows: InspectionScheduleResponse[];
  canManage: boolean;
}

export interface UseScheduleBulkActiveResult {
  rowSelection: MRT_RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>;
  enableRowSelection: boolean;
  /** Toolbar buttons; render inside `TableToolbar.Actions`. */
  actions: React.ReactNode;
  /** Confirm step; render outside the table. */
  confirmDialog: React.ReactNode;
}

const plural = (count: number) => (count === 1 ? '' : 's');

/**
 * Row selection plus bulk activate/deactivate for an inspection-schedule table.
 * @registry Selection state, toolbar buttons and confirm dialog for bulk activating/deactivating inspection schedules.
 */
export const useScheduleBulkActive = ({
  rows,
  canManage,
}: UseScheduleBulkActiveOptions): UseScheduleBulkActiveResult => {
  const {setAlert} = useAlert();
  const [bulkSetActive, {isLoading}] =
    useBulkSetInspectionScheduleActiveMutation();

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pendingActive, setPendingActive] = useState<boolean>();
  const [confirmOpened, confirmHandlers] = useDisclosure(false);

  const rowIdKey = useMemo(() => rows.map(row => row.id).join(','), [rows]);

  useEffect(() => {
    setRowSelection({});
  }, [rowIdKey]);

  const selectedIds = rows
    .filter(row => rowSelection[String(row.id)])
    .map(row => row.id);
  const selectedCount = selectedIds.length;

  const requestBulkActive = (active: boolean) => {
    if (selectedCount === 0) return;
    setPendingActive(active);
    confirmHandlers.open();
  };

  const handleBulkActive = async () => {
    const active = pendingActive;
    confirmHandlers.close();
    if (active === undefined || selectedIds.length === 0) return;

    try {
      const count = await bulkSetActive({ids: selectedIds, active}).unwrap();
      setAlert(
        `Successfully ${active ? 'activated' : 'deactivated'} ${count} schedule${plural(count)}.`,
        'success'
      );
      setRowSelection({});
    } catch (err) {
      setAlert(parseErrorMessage(err), 'error');
    }
    setPendingActive(undefined);
  };

  const actions = canManage ? (
    <>
      <TableToolbar.Action
        variant="secondary"
        onClick={() => requestBulkActive(true)}
        loading={isLoading && pendingActive === true}
        disabled={
          selectedCount === 0 || (isLoading && pendingActive !== true)
        }>{`Activate ${selectedCount}`}</TableToolbar.Action>
      <TableToolbar.Action
        variant="danger"
        onClick={() => requestBulkActive(false)}
        loading={isLoading && pendingActive === false}
        disabled={
          selectedCount === 0 || (isLoading && pendingActive !== false)
        }>{`Deactivate ${selectedCount}`}</TableToolbar.Action>
    </>
  ) : null;

  const confirmDialog = canManage ? (
    <ConfirmDialog
      opened={confirmOpened}
      onClose={() => {
        confirmHandlers.close();
        setPendingActive(undefined);
      }}
      onConfirm={handleBulkActive}
      confirmationButtonColor={pendingActive ? 'yellow' : 'red'}
      title={`${pendingActive ? 'Activate' : 'Deactivate'} ${selectedCount} schedule${plural(selectedCount)}?`}
      body={
        pendingActive
          ? `This will activate ${selectedCount} schedule${plural(selectedCount)}, resuming inspection generation.`
          : 'Deactivated schedules stop generating inspections.'
      }
    />
  ) : null;

  return {
    rowSelection,
    setRowSelection,
    enableRowSelection: canManage,
    actions,
    confirmDialog,
  };
};
